import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-kanban-board>` says about itself.
 *
 * ── ONE LIST, FOUR BODIES ────────────────────────────────────────────────────
 * The board is one of four pieces that draw the SAME record set four ways: `DataTable` as rows and
 * columns, `CardGrid` as cards, this one as stages, `SplitView` as a list beside a reader. The
 * example labelled "The orders list" is the same three orders in all four, so what changes between
 * them is the shape and not the data; `insteadUse` says where each of the other three wins.
 *
 * ── WHAT THE BOARD ANSWERS THAT A TABLE CANNOT ───────────────────────────────
 * Not "what is in this list" but "where is the work". Two consequences follow from the source and
 * are worth stating on the page rather than leaving in a comment: a column is drawn for EVERY value
 * the stage field can take, including the ones nothing is sitting in, because "nothing is waiting on
 * approval" is an answer and a column that vanishes when it empties makes the board a different
 * shape every time you look at it; and records whose stage is blank collect in a leading column
 * rather than being dropped. Both have their own example.
 *
 * ── THE BOARD READS, IT DOES NOT WRITE ───────────────────────────────────────
 * Cards are not draggable. Moving one would be a WRITE, and this piece is a way of reading — so
 * there is no drag event to document, and none is missing.
 */
const docs: ComponentDocs = {
  aliases: ['board', 'kanban', 'swimlanes', 'pipeline view', 'stage view', 'columns view'],

  answers: [
    'show where every record is in a process',
    'see how much work is sitting at each stage',
    'see which stages are empty',
    'open a record from the stage it is sitting at',
  ],

  placements: ['homePage', 'appPage'],

  inputs: [
    {
      name: 'columns',
      type: 'json',
      description:
        'The stages, in the order they should read, each with the stored value it stands for, the ' +
        "label the value set gives it, optionally an intent that colours the column's marker, and " +
        'the cards sitting at it. The app supplies the whole vocabulary — the board never derives ' +
        'the columns from the records, which is what keeps an empty stage visible. Set as a ' +
        'property; an attribute cannot hold a list.',
      required: true,
    },
  ],

  events: [
    {
      name: 'caos-card-open',
      when: 'A card is activated, by click or by keyboard — each card is a real button.',
      carries: '{ id } — the record the card stands for. The host routes it; the board does not navigate.',
    },
  ],

  // NONE, read from the source: the board builds every column and card itself and renders no
  // <slot>, so nothing can be projected into it. What a card shows is what `columns` describes.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the board.' },
    { token: '--caos-color-border', controls: 'The border of a column and of a card at rest.' },
    { token: '--caos-radius', controls: 'The corners of a column.' },
    { token: '--caos-radius-sm', controls: 'The corners of a card.' },
    { token: '--caos-color-surface', controls: 'The ground of a column.' },
    { token: '--caos-color-bg', controls: 'The ground of a card, so it reads as sitting ON the column.' },
    { token: '--caos-color-text-muted', controls: 'The column name, its tally, a card sub-line, and the default marker.' },
    { token: '--caos-color-accent', controls: 'The card title, the border a card takes on hover, and the info marker.' },
    { token: '--caos-color-success', controls: 'The marker of a column whose intent is success.' },
    { token: '--caos-color-warning', controls: 'The marker of a column whose intent is warning.' },
    { token: '--caos-color-danger', controls: 'The marker of a column whose intent is danger.' },
    { token: '--caos-focus-ring', controls: 'The ring on a focused card.' },
    { token: '--caos-space-4', controls: 'The gap between columns.' },
    { token: '--caos-space-3', controls: 'The padding inside a column and inside a card.' },
    { token: '--caos-space-2', controls: 'The gap between the cards in a column, between a column’s marker and its name, and the room below the board for its scrollbar.' },
    { token: '--caos-space-1', controls: 'The gap above a card’s sub-line.' },
  ],

  parts: [
    { part: 'board', is: 'the row of columns' },
    { part: 'column', is: 'one column' },
    { part: 'card', is: 'one card' },
  ],

  methods: [],

  placing:
    'A board is the BODY of a list surface, under the header that names the list. It wants the full ' +
    'width it can get: the columns keep a readable 268px each and the BOARD scrolls sideways when ' +
    'there are more stages than fit, so the page itself never has to. It draws no card around ' +
    'itself, so it sits directly in a page region.',

  whenToUse:
    'A record set that moves through named stages, when the question is how the work is distributed ' +
    'rather than what any one record contains — a pipeline, an approval queue, a build sequence.',

  insteadUse: [
    {
      name: 'DataTable',
      when:
        'The stage is just one more field to read, and what matters is comparing values across ' +
        'records — or selecting, sorting or totalling rows, none of which a board can do.',
    },
    {
      name: 'CardGrid',
      when:
        'The records do not move through stages at all, and a plain grid of cards with a couple of ' +
        'rollups is the whole story.',
    },
    {
      name: 'SplitView',
      when: 'Somebody reads records one after another and wants the selected one open beside the list.',
    },
  ],

  accessibility:
    'Each card is a real button, so it is in the tab order, is activated by Enter and Space, and ' +
    'draws a focus ring. A name too long for its column is truncated with the full text kept as its ' +
    'title. The column marker carries colour ONLY — the stage is named in words beside it, so ' +
    'nothing depends on telling the colours apart. Left to the author: the board has no landmark or ' +
    'accessible name of its own, so the surface around it has to say what list this is; and the ' +
    "column labels come from the value set, so they read to a screen-reader user exactly as they " +
    'are worded there.',

  examples: [
    {
      key: 'orders',
      label: 'The orders list',
      summary:
        'The same three orders the other three list bodies draw, stood up by stage — including one ' +
        'stage nothing is sitting in, which is kept rather than dropped.',
      placement: {
        component: 'caos-kanban-board',
        inputs: {
          columns: [
            { value: 'draft', label: 'Draft', cards: [{ id: 'o1', title: 'ORD-4471', subtitle: 'Meridian Supply' }] },
            { value: 'review', label: 'In review', intent: 'info', cards: [{ id: 'o3', title: 'ORD-4473', subtitle: 'Brightwater Group' }] },
            { value: 'blocked', label: 'Blocked', intent: 'warning', cards: [] },
            { value: 'approved', label: 'Approved', intent: 'success', cards: [{ id: 'o2', title: 'ORD-4472', subtitle: 'Calder & Finch' }] },
          ],
        },
      },
    },
    {
      key: 'unset',
      label: 'A record with no stage',
      summary:
        'A fourth order whose stage has never been set: it collects in a leading column standing for ' +
        'the blank value, because a record missing from a board it belongs on is the worst outcome ' +
        'here.',
      placement: {
        component: 'caos-kanban-board',
        inputs: {
          columns: [
            { value: '', label: 'Unset', cards: [{ id: 'o9', title: 'ORD-4479', subtitle: 'Halstead Holdings' }] },
            { value: 'draft', label: 'Draft', cards: [{ id: 'o1', title: 'ORD-4471', subtitle: 'Meridian Supply' }] },
            { value: 'review', label: 'In review', intent: 'info', cards: [{ id: 'o3', title: 'ORD-4473', subtitle: 'Brightwater Group' }] },
            { value: 'cancelled', label: 'Cancelled', intent: 'danger', cards: [] },
          ],
        },
      },
    },
  ],
};

export default docs;
